import { getJWTToken } from '../../utils/auth';
import type { DomainBenchmark, FlashlightDomainKey } from './flashlight';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type FlashlightWindowMonths = { boy?: string; moy?: string; eoy?: string };
export type FlashlightSchemeLevel = { score: number; label: string; color: string };
export type FlashlightBenchmarkYear = { schoolYear: number; benchmarks: DomainBenchmark[]; windows?: FlashlightWindowMonths; scheme?: FlashlightSchemeLevel[] };
export type FlashlightBenchmarkSource = { database: string; status: string };

export type FlashlightBenchmarksResponse = {
  hasData: boolean;
  years: FlashlightBenchmarkYear[];
  sources: FlashlightBenchmarkSource[];
  scheme?: FlashlightSchemeLevel[];
  rubricUncertain?: boolean;
};

export type FlashlightAssignmentRow = {
  assignmentId: string;
  name: string;
  date: string;
  subDomain: string;
  score: number;
};
export type FlashlightAssignmentDomain = { domain: FlashlightDomainKey; assignments: FlashlightAssignmentRow[] };
export type FlashlightAssignmentYear = { schoolYear: number; assignments: FlashlightAssignmentDomain[] };

export type FlashlightAssignmentsResponse = {
  hasData: boolean;
  years: FlashlightAssignmentYear[];
  sources: FlashlightBenchmarkSource[];
};

export class FlashlightBenchmarksFetchError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'FlashlightBenchmarksFetchError';
    this.status = status;
  }
}

async function fetchStudentResource<T>(studentId: string, resource: string, query = ''): Promise<T> {
  const token = await getJWTToken();
  const response = await fetch(
    `${apiUrl}/students/${encodeURIComponent(studentId)}/${resource}${query}`,
    { headers: authHeaders(token) },
  );
  if (!response.ok) {
    throw new FlashlightBenchmarksFetchError(
      `${resource} fetch failed: ${response.status}`,
      response.status,
    );
  }
  return (await response.json()) as T;
}

export function fetchFlashlightBenchmarks(studentId: string, asOf?: string): Promise<FlashlightBenchmarksResponse> {
  return fetchStudentResource<FlashlightBenchmarksResponse>(studentId, 'flashlight-benchmarks', asOf ? `?asOf=${encodeURIComponent(asOf)}` : '');
}

export function fetchFlashlightAssignments(studentId: string): Promise<FlashlightAssignmentsResponse> {
  return fetchStudentResource<FlashlightAssignmentsResponse>(studentId, 'flashlight-assignments');
}
